/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { getHistoryAPICall } from "../../api/history/getHistory";
import { addHistoryAPI } from "../../api/history/saveHistory";
import { deleteHistoryAPI } from "../../api/history/deleteHistory";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { getItemFromLocalStorage, setItemInLocalStorage } from "../../hooks/localStorage";
import { GetHistoryResponse } from "../../interface";
import { sortDatesInAscendingOrder } from "../../utils/dateUtils";
import { StateContext } from "../globalContext/context";
import { iGlobalContext } from "../globalContext/interface";
import { iHistoryContext } from "./interface";
import { consoleLog } from "../../utils/consoleLog/consoleLog";
import { getChatTitle } from "../../utils/chat/chatTitle";
import { isAdminUser } from "../../utils/isAdminUser";
import { withoutChatCheckout } from "../../utils/chat/chatCheckout";

// Debounced delete-backfill tuning. After a burst of deletes settles, ONE
// top-up call refills the list (the infinite-scroll observer won't re-trigger
// on its own once the loader is already in view).
const BACKFILL_DEBOUNCE_MS = 350;
const MIN_BACKFILL = 3;

// The newest history rows from the last successful first-page fetch, so the
// sidebar paints them on boot instead of waiting for /getHistory (the rows were
// the page's LCP element). Full items, since a row click opens the chat from
// `item.history`; capped so long conversations can't exhaust localStorage.
// Keyed by user id, encrypted like the other user data in localStorage, and
// cleared on logout (logoutClearStorage).
export const HISTORY_CACHE_KEY = "_zenvoya_history_cache";
const HISTORY_CACHE_MAX_ITEMS = 10;
const HISTORY_CACHE_MAX_CHARS = 500_000;

const readHistoryCache = (userId: string): any[] | null => {
  const cached = getItemFromLocalStorage(HISTORY_CACHE_KEY);
  return cached?.userId === userId && Array.isArray(cached.items) ? cached.items : null;
};

const writeHistoryCache = (userId: string, items: any[]) => {
  try {
    let rows = (items || [])
      .filter((ele: any) => Array.isArray(ele?.history) && ele.history.length > 0)
      .slice(0, HISTORY_CACHE_MAX_ITEMS);
    while (rows.length > 0 && JSON.stringify(rows).length > HISTORY_CACHE_MAX_CHARS) rows = rows.slice(0, -1);
    if (rows.length > 0) setItemInLocalStorage(HISTORY_CACHE_KEY, { userId, items: rows });
    else localStorage.removeItem(HISTORY_CACHE_KEY);
  } catch {
    // Quota / private mode — the sidebar just waits for the API as before.
  }
};

const HistoryContext = createContext({} as iHistoryContext);
export const HistoryContextProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [todayHistory, setTodayHistory] = useState<any>([]);
  const [yesterdayHistory, setYesterdayHistory] = useState<any>([]);
  const [oneWeekAgoHistory, setOneWeekAgoHistory] = useState<any>([]);
  const [lastOneMonth, setLastOneMonth] = useState<any>([]);
  const globalContext = useContext(StateContext);
  const { userInfo, accessJWT, isAdminChat }: iGlobalContext = globalContext;
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [skipLimitHistory, setSkipLimitHistory] = useState({
    skip: 0,
    limit: 20,
  });
  // True when the last initial/reload fetch FAILED (null from getHistoryAPICall
  // = invalid/expired JWT, network error, 5xx, or decrypt failure) — as opposed
  // to a genuinely-empty account. Lets the UI show a "couldn't load — retry"
  // state instead of the misleading "No trips planned yet" empty state.
  const [historyLoadFailed, setHistoryLoadFailed] = useState(false);
  const { ref: historyPaginationLoaderRef, inView } = useInView({});

  // Bumped after every real data append (page load / delete-backfill) — NOT on
  // the optimistic delete. Drives the auto-fill effect so a short list keeps
  // loading page-by-page until the loader leaves the viewport (scrollable),
  // without coupling to the delete path's bucket updates.
  const [pageLoadedTick, setPageLoadedTick] = useState(0);

  // In-flight guard. Multiple sources can fire getHistory concurrently
  // (sidebar click handler in useDesktopSideBarLogic, HistoryTab mount-effect,
  // pagination `inView`, deletes). Without this, two concurrent calls both
  // read the same `skipLimitHistory` closure and write the same next-skip —
  // causing skipped pages, duplicate fetches, or "results not showing" when
  // a faster second response is overwritten by a slower first one.
  // Reload takes priority: a reload call bypasses the guard and supersedes
  // any in-flight pagination call.
  const inFlightRef = useRef(false);

  // True while the list shows rows seeded from HISTORY_CACHE_KEY on boot.
  const seededFromCacheRef = useRef(false);

  const currentUserIdRef = useRef(userInfo?.id);
  currentUserIdRef.current = userInfo?.id;

  // Ids whose DELETE is in-flight / just optimistically removed. Used to filter
  // a server-lagged getHistory response so a just-deleted item can't flash back.
  const pendingDeleteIdsRef = useRef<Set<string>>(new Set());
  const pendingTitlesRef = useRef<Map<string, string>>(new Map());

  // Debounced delete-backfill state. `debounceTimerRef` is the trailing top-up
  // timer; `deletedSinceTopUpRef` is the burst size (sizes the next fetch);
  // `nextBackfillOffsetRef` is the live loaded-count to fetch from (set
  // synchronously on each delete, immune to stale closures).
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deletedSinceTopUpRef = useRef(0);
  const nextBackfillOffsetRef = useRef(0);

  // Ids of truly-empty history rows (`history: []`) we've already fired a
  // background silent-delete for, so we don't re-fire across refetches.
  const autoDeletedEmptyIdsRef = useRef<Set<string>>(new Set());

  function resetState() {
    setTodayHistory((prev: any) => []);
    setYesterdayHistory((prev: any) => []);
    setOneWeekAgoHistory((prev: any) => []);
    setLastOneMonth((prev: any) => []);
    setSkipLimitHistory({
      skip: 0,
      limit: 20,
    });
    setHasMoreHistory(true);
    // Cancel any pending delete-backfill so a logout/reload doesn't fire a
    // stale top-up against the cleared list.
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = null;
    deletedSinceTopUpRef.current = 0;
    nextBackfillOffsetRef.current = 0;
  }

  useEffect(() => {
    if (!userInfo?.id) {
      resetState();
    }
  }, [userInfo]);

  // Clear the debounced backfill timer on unmount (hygiene; App-level context).
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Pagination + auto-fill. Fires when the bottom loader is in view — either on
  // a normal scroll-to-bottom (inView off→on) OR right after a load lands
  // (pageLoadedTick) while the loader is STILL on-screen because the list is too
  // short to scroll. The latter makes a short first page (5) auto-load the next
  // page(s) until the list overflows (inView flips false = scrollable) or there's
  // no more history. getHistory self-guards (inFlightRef / userId / hasMore), so
  // redundant fires are no-ops; a failed fetch doesn't bump the tick, so it can't
  // spin. (Deps deliberately exclude the bucket arrays so the optimistic delete
  // doesn't re-trigger this and break the delete-backfill debounce.)
  useEffect(() => {
    if (inView && hasMoreHistory) getHistory();
  }, [inView, hasMoreHistory, pageLoadedTick]);

  // Boot: paint the cached newest rows before the first frame; the first
  // /getHistory page then replaces them (see `replaceSeed` in getHistory).
  useLayoutEffect(() => {
    if (!userInfo?.id) return;
    const cached = readHistoryCache(userInfo.id);
    if (!cached?.length) return;
    seededFromCacheRef.current = true;
    appendHistoryItems(cached);
  }, []);

  function withPendingTitle(ele: any) {
    const pendingTitle = pendingTitlesRef.current.get(ele?.id);
    if (!pendingTitle) return ele;
    if (ele?.title === pendingTitle) {
      pendingTitlesRef.current.delete(ele.id);
      return ele;
    }
    return { ...ele, title: pendingTitle };
  }

  function patchHistoryTitle(id: string, title: string) {
    if (!id || !title) return;
    pendingTitlesRef.current.set(id, title);
    const patch = (arr: any) => (Array.isArray(arr) ? arr.map((e: any) => (e?.id === id ? { ...e, title } : e)) : arr);
    setTodayHistory(patch);
    setYesterdayHistory(patch);
    setOneWeekAgoHistory(patch);
    setLastOneMonth(patch);
  }

  // Shared bucketing/merge: filters out pending-deleted ids, splits items into
  // the 4 date buckets, and merges+dedups (by id) into existing state. Used by
  // both getHistory (page fetch) and runBackfill (delete top-up) so the
  // bucketing logic lives in exactly one place.
  function appendHistoryItems(items: any[]) {
    // Drop any items whose delete is still in-flight / just optimistically
    // removed, so a slow server response can't re-surface a deleted item
    // (covers the mount-effect reload on return + post-save refetches).
    const incoming = (items || [])
      .filter((ele: any) => !pendingDeleteIdsRef.current.has(ele?.id))
      .map(withPendingTitle);

    // Silently prune truly-empty history rows (`history: []`): hide them from
    // the list AND fire a background delete (no toast / loader / GA). Guarded so
    // the currently-open chat is never touched and each id is deleted at most
    // once per session (a failed delete isn't retried — the row stays hidden).
    const visibleResponse = incoming.filter((ele: any) => {
      const isEmpty = !Array.isArray(ele?.history) || ele.history.length === 0;
      if (!isEmpty) return true;
      const emptyId = ele?.id;
      if (emptyId && emptyId !== currentHistoryId && !autoDeletedEmptyIdsRef.current.has(emptyId)) {
        autoDeletedEmptyIdsRef.current.add(emptyId);
        // fire-and-forget background cleanup; errors swallowed (row stays hidden)
        deleteHistoryAPI(emptyId, accessJWT).catch(() => {});
      }
      return false; // never show an empty row
    });

    const now = new Date();

    // Bucket boundaries (local time). The four buckets are mutually EXCLUSIVE
    // and EXHAUSTIVE — every item lands in exactly one, so the rendered count can
    // never drop below what the API sent:
    //   • Today is open-ended at the TOP, so a future / clock-skewed timestamp
    //     still shows (under Today) instead of vanishing.
    //   • "Last 30 days" is the open-ended catch-all at the bottom AND absorbs
    //     any item whose createdAt is missing/unparseable (NaN).
    //   • The middle two are half-open ranges with no gaps between them.
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
    const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0).getTime();
    const startOfWeekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0).getTime();
    const ts = (ele: any) => new Date(ele?.createdAt).getTime();

    setTodayHistory((prev: any) => {
      const merged = [
        ...prev,
        ...sortDatesInAscendingOrder(
          // Today and anything dated in the future (clock skew / bad data).
          visibleResponse.filter((ele: any) => Number.isFinite(ts(ele)) && ts(ele) >= startOfToday),
          "createdAt"
        ),
      ];

      // Remove duplicates based on the `id` field
      const uniqueHistory = Array.from(new Map(merged.map((item: any) => [item.id, item])).values());

      return uniqueHistory;
    });
    setYesterdayHistory((prev: any) => {
      const merged = [
        ...prev,
        ...sortDatesInAscendingOrder(
          visibleResponse.filter(
            (ele: any) => Number.isFinite(ts(ele)) && ts(ele) >= startOfYesterday && ts(ele) < startOfToday
          ),
          "createdAt"
        ),
      ];

      // Remove duplicates based on the `id` field
      const uniqueHistory = Array.from(new Map(merged.map((item: any) => [item.id, item])).values());

      return uniqueHistory;
    });

    setOneWeekAgoHistory((prev: any) => {
      const merged = [
        ...prev,
        ...sortDatesInAscendingOrder(
          // Half-open range [startOfWeekAgo, startOfYesterday): older than
          // "Yesterday" down to a week ago. No gap with Yesterday above.
          visibleResponse.filter(
            (ele: any) => Number.isFinite(ts(ele)) && ts(ele) >= startOfWeekAgo && ts(ele) < startOfYesterday
          ),
          "createdAt"
        ),
      ];

      // Remove duplicates based on the `id` field
      const uniqueHistory = Array.from(new Map(merged.map((item: any) => [item.id, item])).values());

      return uniqueHistory;
    });

    setLastOneMonth((prev: any) => {
      const merged = [
        ...prev,
        ...sortDatesInAscendingOrder(
          // Catch-all: everything older than a week, PLUS any item with a
          // missing/unparseable createdAt (NaN) — guarantees nothing is lost.
          visibleResponse.filter((ele: any) => !Number.isFinite(ts(ele)) || ts(ele) < startOfWeekAgo),
          "createdAt"
        ),
      ];

      // Remove duplicates based on the `id` field
      const uniqueHistory = Array.from(new Map(merged.map((item: any) => [item.id, item])).values());

      return uniqueHistory;
    });

    // Signal "a real load just landed" so the auto-fill effect re-checks whether
    // the list is now tall enough to scroll. Only bumped on real appends
    // (getHistory / runBackfill) — never on the optimistic delete — so it can't
    // interfere with the delete-backfill debounce.
    setPageLoadedTick((t) => t + 1);
  }

  async function getHistory(reload?: boolean, openWithSideBar?: boolean, attempt: number = 0): Promise<void> {
    if ((!userInfo?.id || !hasMoreHistory) && !openWithSideBar && !reload) return;
    // In-flight guard. A pagination (inView) call shouldn't queue behind
    // another pagination call; a reload always proceeds and supersedes any
    // in-flight pagination.
    if (inFlightRef.current && !reload) return;
    inFlightRef.current = true;

    // NOTE: we intentionally do NOT resetState() before the await on reload.
    // Clearing the buckets up-front meant a FAILED reload (invalid/expired JWT,
    // network blip, 5xx, decrypt failure — getHistoryAPICall returns null in ALL
    // those cases) wiped the already-loaded list and rendered the "No trips
    // planned yet" empty state, so a user's existing conversations LOOKED gone.
    // We now keep the current list during the fetch and reset only on a
    // SUCCESSFUL reload (atomic replace below); a failure leaves the list intact.
    setIsLoading(true);
    // Capture skip/limit BEFORE await so the functional updater below can
    // pivot off this snapshot rather than a closure-stale value when another
    // call mutated state mid-flight.
    const reqSkip = reload ? 0 : skipLimitHistory?.skip;
    const reqLimit = reload && skipLimitHistory?.skip > 0 ? skipLimitHistory?.skip : skipLimitHistory?.limit;

    const reqUserId = userInfo?.id;

    let response: any;
    try {
      response = await getHistoryAPICall(accessJWT, reqUserId, reqSkip, reqLimit);
    } finally {
      inFlightRef.current = false;
    }

    if (currentUserIdRef.current !== reqUserId) return;

    if (!response) {
      // null = a FAILED fetch (bad JWT / network / 5xx / decrypt / out-of-range
      // skip), NOT "no history". Retry once for ANY fetch — initial OR pagination
      // — to ride out a transient blip before giving up.
      if (attempt < 1) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        if (currentUserIdRef.current !== reqUserId) return;
        return getHistory(reload, openWithSideBar, attempt + 1);
      }
      setIsLoading(false);
      if (reload || openWithSideBar) {
        // Initial/reload load: surface the distinct error/retry state instead of
        // the genuinely-empty "No trips planned yet" view.
        setHistoryLoadFailed(true);
      } else {
        // Pagination ("load more") failed even after the retry — STOP the bottom
        // skeleton so it can't spin forever (the reported "stuck loading for
        // hours"). The already-loaded list stays intact; re-opening History
        // reloads the rest. A failed page must NEVER leave hasMoreHistory=true
        // with nothing in flight, or the loader never resolves.
        setHasMoreHistory(false);
      }
      return;
    }

    // Success.
    setHistoryLoadFailed(false);
    setIsLoading(false);
    // The first fresh page after a cache seed replaces the cached rows (a chat
    // deleted on another device must not linger), same as a reload.
    const replaceSeed = seededFromCacheRef.current && reqSkip === 0;
    if (reqSkip === 0) {
      seededFromCacheRef.current = false;
      if (reqUserId) writeHistoryCache(reqUserId, response);
    }
    // Atomic replace on reload: clear THEN re-populate in the same React batch.
    // Queued setState applies in order, so the functional updaters below see the
    // just-cleared ([]) state — no stale-mixed-with-new, and no blank window
    // while the request was in flight (the old list stayed visible until now).
    if (reload || replaceSeed) resetState();
    // End of the list = the backend returned a SHORT page (fewer items than we
    // asked for), not only an exactly-empty one. The old `=== 0` check left
    // hasMoreHistory=true after a final partial page (e.g. 3 items for a page
    // size of 5 — i.e. any account with fewer items than the page limit), so the
    // bottom pagination skeleton spun forever ("infinite loading") and a doomed
    // next-page request kept firing. `< reqLimit` covers the empty case too and
    // never drops data (a short page genuinely has no more rows after it).
    if (response.length < reqLimit) {
      setHasMoreHistory(false);
    }

    // Functional updater so concurrent calls don't both compute next-skip
    // from the same stale snapshot. The first `limit` is 5; subsequent pages
    // bump it to 10 (preserving prior behavior).
    setSkipLimitHistory((prev) => ({
      skip: prev.skip + prev.limit,
      limit: 10,
    }));

    appendHistoryItems(response);
  }

  /**
   * Optimistic delete of a single history item.
   * Removes the item from every date bucket immediately (no skeleton, no
   * refetch), fires the DELETE API in the background, and rolls back on
   * failure. Mirrors EngagementContext.deleteComment's snapshot-and-restore.
   * Returns { success, statusCode } so the caller handles toasts + GA.
   */
  async function deleteHistoryItem(item: any): Promise<{ success: boolean; statusCode?: number }> {
    const id = item?.id;
    if (!id) return { success: false };

    // Snapshot for rollback (buckets + pagination offset).
    const snapshotToday = todayHistory;
    const snapshotYesterday = yesterdayHistory;
    const snapshotWeek = oneWeekAgoHistory;
    const snapshotMonth = lastOneMonth;
    const snapshotSkipLimit = skipLimitHistory;

    // Loaded count once this item is removed (exactly one item leaves a bucket).
    const newLoadedCount = Math.max(
      0,
      (snapshotToday?.length || 0) +
        (snapshotYesterday?.length || 0) +
        (snapshotWeek?.length || 0) +
        (snapshotMonth?.length || 0) -
        1
    );

    // Mark in-flight (so a concurrent getHistory can't re-surface it) and
    // optimistically remove from every bucket via functional updaters.
    pendingDeleteIdsRef.current.add(id);
    const filterOut = (arr: any) => (Array.isArray(arr) ? arr.filter((e: any) => e?.id !== id) : arr);
    setTodayHistory((prev: any) => filterOut(prev));
    setYesterdayHistory((prev: any) => filterOut(prev));
    setOneWeekAgoHistory((prev: any) => filterOut(prev));
    setLastOneMonth((prev: any) => filterOut(prev));
    // Keep the scroll offset == loaded count. The removed item shifts every
    // later server offset down by one, so without this the next pagination
    // fetch would start too far ahead and skip items (gaps).
    setSkipLimitHistory((prev) => ({ ...prev, skip: newLoadedCount }));

    const rollback = () => {
      setTodayHistory(snapshotToday);
      setYesterdayHistory(snapshotYesterday);
      setOneWeekAgoHistory(snapshotWeek);
      setLastOneMonth(snapshotMonth);
      setSkipLimitHistory(snapshotSkipLimit);
    };

    try {
      const response = await deleteHistoryAPI(id, accessJWT);
      if (response?.statusCode === 200) {
        pendingDeleteIdsRef.current.delete(id);
        // Debounced top-up: coalesce a burst of deletes into ONE backfill so
        // the list refills instead of running dry (the inView observer won't
        // re-trigger once its loader is already on-screen). The offset is the
        // post-burst loaded count; the timer fires after deletes settle.
        nextBackfillOffsetRef.current = newLoadedCount;
        deletedSinceTopUpRef.current += 1;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          runBackfill();
        }, BACKFILL_DEBOUNCE_MS);
        return { success: true, statusCode: 200 };
      }
      // Non-200 / null → undo the optimistic removal.
      rollback();
      pendingDeleteIdsRef.current.delete(id);
      return { success: false, statusCode: response?.statusCode };
    } catch (error) {
      rollback();
      pendingDeleteIdsRef.current.delete(id);
      return { success: false };
    }
  }

  /**
   * Debounced top-up after deletes. Fetches the next chunk from the current
   * loaded offset and APPENDS it (no resetState, no skeleton), so the list
   * refills itself instead of running dry. A burst of deletes coalesces into a
   * single request sized to the burst (min MIN_BACKFILL, so single deletes
   * still grow the list). Deliberately does NOT advance `skipLimitHistory.skip`
   * — `skip` already == loaded count (resynced per delete); the next scroll
   * realigns via its own `skip += limit`, so there are never gaps (only a tiny,
   * deduped re-fetch overlap).
   */
  async function runBackfill() {
    if (!userInfo?.id || !hasMoreHistory) {
      deletedSinceTopUpRef.current = 0;
      return;
    }
    // Don't fight an in-flight scroll/reload fetch; retry once after the window
    // (keeps the pending burst count intact).
    if (inFlightRef.current) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        runBackfill();
      }, BACKFILL_DEBOUNCE_MS);
      return;
    }
    const count = Math.max(MIN_BACKFILL, deletedSinceTopUpRef.current);
    deletedSinceTopUpRef.current = 0;
    const offset = nextBackfillOffsetRef.current;
    inFlightRef.current = true;
    const reqUserId = userInfo?.id;
    let resp: any;
    try {
      resp = await getHistoryAPICall(accessJWT, reqUserId, offset, count);
    } finally {
      inFlightRef.current = false;
    }
    if (currentUserIdRef.current !== reqUserId) return;
    if (!resp || resp.length === 0) {
      setHasMoreHistory(false);
      return;
    }
    appendHistoryItems(resp);
    if (resp.length < count) setHasMoreHistory(false);
  }

  /**
   * Save history
   */
  const [currentHistoryId, setCurrentHistoryId] = useSessionStorage("_trip_trove_current_history_id", "");
  const [callSaveHistoryAPIFlag, setCallSaveHistoryAPIFlag] = useState({
    flag: false,
    method: "",
  });
  async function saveHistory(chatDataArgs: any, method: "POST" | "PUT", userId?: string) {
    const isUserId = userInfo?.id || userId;
    if (!chatDataArgs || !isUserId) return;
    consoleLog(["SAVING CHAT A", userInfo?.id || userId || ""]);

    // A PUT can only update a row via its id. Without a currentHistoryId, a "PUT"
    // would go out with no id and the backend INSERTs a duplicate row instead of
    // updating — so downgrade an id-less PUT to a POST (create). Method follows
    // id at this single chokepoint, covering all callers.
    const effectiveMethod: "POST" | "PUT" = method === "PUT" && !currentHistoryId ? "POST" : method;
    const title = getChatTitle(chatDataArgs);
    const savedHistory = Array.isArray(chatDataArgs) ? withoutChatCheckout(chatDataArgs) : chatDataArgs;

    const historyData: any =
      effectiveMethod === "PUT" && currentHistoryId
        ? {
            id: currentHistoryId,
            userId: userInfo?.id || userId || "",
            history: savedHistory,
            ...(title ? { title } : {}),
          }
        : {
            userId: userInfo?.id || userId || "",
            history: savedHistory,
            isPublic: isAdminUser(userInfo) && isAdminChat,
            ...(title ? { title } : {}),
          };

    if (userInfo?.id || userId) {
      // Per-call AbortController — do NOT reuse the shared streaming/airport
      // abortControllerRef. Sharing it let a cancelled stream / Back press abort
      // an in-flight save (and saveHistory clobbering the shared ref could cancel
      // streaming). History writes must be decoupled from those flows.
      const saveAbort = new AbortController();
      const response: GetHistoryResponse | any = await addHistoryAPI(
        accessJWT,
        historyData,
        effectiveMethod,
        saveAbort.signal
      );
      // Only adopt a REAL id. POST returns the saved entity ({ id }); PUT returns
      // an array (no `.id`); a failed/aborted/expired-JWT save returns null. The
      // old unconditional setCurrentHistoryId(response?.data?.id) wiped the id to
      // undefined on every successful PUT and every failure, after which all
      // further PUTs went out id-less → lost updates / duplicate rows.
      if (response?.data?.id) setCurrentHistoryId(response.data.id);
    }
  }

  const value = useMemo(
    () => ({
      hasMoreHistory,
      historyPaginationLoaderRef,
      getHistory,
      deleteHistoryItem,
      isLoading,
      todayHistory,
      yesterdayHistory,
      oneWeekAgoHistory,
      lastOneMonth,
      saveHistory,
      callSaveHistoryAPIFlag,
      setCallSaveHistoryAPIFlag,
      currentHistoryId,
      setCurrentHistoryId,
      historyLoadFailed,
      patchHistoryTitle,
    }),
    [
      hasMoreHistory,
      historyPaginationLoaderRef,
      getHistory,
      deleteHistoryItem,
      isLoading,
      todayHistory,
      yesterdayHistory,
      oneWeekAgoHistory,
      lastOneMonth,
      saveHistory,
      callSaveHistoryAPIFlag,
      setCallSaveHistoryAPIFlag,
      currentHistoryId,
      setCurrentHistoryId,
      historyLoadFailed,
      patchHistoryTitle,
    ]
  );
  return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>;
};
export const useHistoryContext = () => useContext(HistoryContext);
